/* * Version: 3.3.0
 * Letzte Änderung: 16.09.2026, 14:00
 * Änderungshistorie: Frontend-Validierungs-Bug behoben, indem die Standardwerte für die Mannschafts-Dropdowns (classification und singleClassification) im useEffect-Hook initialisiert werden.
 * Copyright: BAIT-Tech
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api';
import { CLASSIFICATIONS } from '../constants/classificationConstants';
import { GAME_MODES } from '../constants/gameModeConstants';
import EditEntryModal from '../components/EditEntryModal';
import EditTeamModal from '../components/EditTeamModal';
import { toast } from 'react-toastify';

const PAIR_GAME_MODES = ['PAARBEWERB', 'TANDEM'];

const TournamentDetailScreen = () => {
  const { id: tournamentId } = useParams();

  const [tournament, setTournament] = useState(null);
  const [entries, setEntries] = useState([]);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState({});

  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamClassification, setNewTeamClassification] = useState('');
  const [newTeamSingleClassification, setNewTeamSingleClassification] = useState('');
  const [newTeamPlayers, setNewTeamPlayers] = useState(['', '', '', '']);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [selectedPlayer2, setSelectedPlayer2] = useState('');
  const [selectedClassification, setSelectedClassification] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedGameMode, setSelectedGameMode] = useState('');

  const [isCreatingGuest, setIsCreatingGuest] = useState(false);
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestClubName, setGuestClubName] = useState('');
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [tournamentRes, entriesRes, playersRes, teamsRes] = await Promise.all([
        apiClient.get(`/api/tournaments/${tournamentId}`),
        apiClient.get(`/api/tournaments/${tournamentId}/entries`),
        apiClient.get('/api/players'),
        apiClient.get(`/api/tournaments/${tournamentId}/teams`),
      ]);
      setTournament(tournamentRes.data);
      setEntries(entriesRes.data);
      setPlayers(playersRes.data);
      setTeams(teamsRes.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Turnierdaten.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tournamentId]);
  
  const { teamClassifications, singleClassifications, unassignedStammPlayers, unassignedStammPlayersForPair, teamCount, singleCount, availableGameModes } = useMemo(() => {
    if (!tournament || !players) return { teamClassifications: [], singleClassifications: [], unassignedStammPlayers: [], unassignedStammPlayersForPair: [], teamCount: 0, singleCount: 0, availableGameModes: [] };
    
    const assignedPlayerIdsInCurrentMode = new Set();
    entries.forEach(entry => {
      if (entry.gameMode === selectedGameMode) {
        if (entry.player?._id) assignedPlayerIdsInCurrentMode.add(entry.player._id);
        if (entry.player2?._id) assignedPlayerIdsInCurrentMode.add(entry.player2._id);
      }
    });
    
    const availablePlayers = players.filter(p => !assignedPlayerIdsInCurrentMode.has(p._id));
    const availablePlayersForPair = availablePlayers.filter(p => p._id !== selectedPlayer);

    const teamIdsWithEntries = new Set(entries.filter(e => e.team).map(e => e.team._id));
    const teamCount = teamIdsWithEntries.size;
    const singleCount = entries.filter(e => !e.team).length;

    return {
      teamClassifications: tournament.teamClassifications || [],
      singleClassifications: tournament.classifications || [],
      unassignedStammPlayers: availablePlayers,
      unassignedStammPlayersForPair: availablePlayersForPair,
      teamCount,
      singleCount,
      availableGameModes: tournament.gameModes || [],
    };
  }, [tournament, players, entries, selectedPlayer, selectedGameMode]);
  
  // Dieser useEffect-Hook synchronisiert den Formular-State mit den verfügbaren Optionen
  useEffect(() => {
    if (loading) return; // Nichts tun, während die Daten noch laden

    // Setze Standard für Spielmodus (Einzel/Paar)
    if (availableGameModes.length > 0 && !availableGameModes.includes(selectedGameMode)) {
      setSelectedGameMode(availableGameModes[0]);
    }

    // Setze Standard für Wertung (Einzel/Paar)
    if (singleClassifications.length > 0 && !singleClassifications.includes(selectedClassification)) {
      setSelectedClassification(singleClassifications[0]);
    }

    // NEU: Setze Standards für die Mannschafts-Dropdowns, um den Validierungsfehler zu verhindern
    if (teamClassifications.length > 0 && !teamClassifications.includes(newTeamClassification)) {
      setNewTeamClassification(teamClassifications[0]);
    }
    if (singleClassifications.length > 0 && !singleClassifications.includes(newTeamSingleClassification)) {
      setNewTeamSingleClassification(singleClassifications[0]);
    }

    // Setze Standard für Spieler 1, falls der aktuelle nicht mehr verfügbar ist oder keiner gewählt wurde
    const currentPlayer1StillAvailable = unassignedStammPlayers.some(p => p._id === selectedPlayer);
    if (unassignedStammPlayers.length > 0 && !currentPlayer1StillAvailable) {
      setSelectedPlayer(unassignedStammPlayers[0]._id);
    } else if (unassignedStammPlayers.length === 0) {
      setSelectedPlayer('');
    }

    // Setze Standard für Spieler 2 zurück, wenn er nicht mehr verfügbar ist
    const currentPlayer2StillAvailable = unassignedStammPlayersForPair.some(p => p._id === selectedPlayer2);
    if (!currentPlayer2StillAvailable) {
      setSelectedPlayer2('');
    }

  }, [loading, availableGameModes, singleClassifications, teamClassifications, unassignedStammPlayers, unassignedStammPlayersForPair, selectedGameMode, selectedClassification, newTeamClassification, newTeamSingleClassification, selectedPlayer, selectedPlayer2]);

  const getLabel = (key, constantList) => constantList.find(c => c.key === key)?.label || key;

  const toggleTeamExpansion = (teamId) => {
    setExpandedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  const handleTeamPlayerNameChange = (index, value) => {
    const updatedPlayers = [...newTeamPlayers];
    updatedPlayers[index] = value;
    setNewTeamPlayers(updatedPlayers);
  };

  const handleCreateTeamWithPlayers = async (e) => {
    e.preventDefault();
    // Frontend-Validierung: Alle Felder müssen befüllt sein
    if (!newTeamName || !newTeamClassification || !newTeamSingleClassification) { 
      toast.error("Bitte alle Felder für die Mannschaft ausfüllen."); 
      return; 
    }
    
    const playersToCreate = newTeamPlayers.filter(p => p.trim() !== '');
    try {
      const defaultGameMode = availableGameModes.length > 0 ? availableGameModes[0] : 'CLASSIC_120';
      const { data } = await apiClient.post(`/api/tournaments/${tournamentId}/teams/teams_with_players`, { name: newTeamName, classification: newTeamClassification, singleClassification: newTeamSingleClassification, players: playersToCreate, gameMode: defaultGameMode });
      
      fetchData(); 
      toast.success(data.message); 
      setNewTeamName(''); 
      setNewTeamPlayers(['', '', '', '']);
    } catch (err) { 
      toast.error(err.response?.data?.message || 'Fehler beim Erstellen der Mannschaft.'); 
    }
  };

  const handleUpdateTeam = async (teamId, updatedData) => {
    try { 
      await apiClient.put(`/api/teams/${teamId}`, updatedData); 
      fetchData(); 
      setEditingTeam(null); 
      toast.success("Mannschaft erfolgreich aktualisiert."); 
    } catch (err) { 
      toast.error(err.response?.data?.message || "Fehler beim Aktualisieren der Mannschaft."); 
    }
  };
  
  const handleDeleteTeam = async (teamId) => {
    if (window.confirm('Mannschaft und alle zugehörigen Spielernennungen löschen?')) {
      try { 
        await apiClient.delete(`/api/teams/${teamId}`); 
        fetchData(); 
        toast.success('Mannschaft erfolgreich gelöscht.'); 
      } catch (err) { 
        toast.error('Fehler beim Löschen der Mannschaft.'); 
      }
    }
  };
  
  const handleCreateEntry = async (e) => {
    e.preventDefault();
    let playerIdToEnter = selectedPlayer;
    let player2IdToEnter = PAIR_GAME_MODES.includes(selectedGameMode) ? selectedPlayer2 : null;
    
    if (isCreatingGuest) {
      if (!guestFirstName || !guestLastName) { 
        return toast.error("Vor- und Nachname des Gastspielers sind erforderlich."); 
      }
      try {
        const { data: newPlayer } = await apiClient.post('/api/players', { firstName: guestFirstName, lastName: guestLastName, guestClubName: guestClubName });
        setPlayers(prev => [...prev, newPlayer]); 
        playerIdToEnter = newPlayer._id; 
        setGuestFirstName(''); 
        setGuestLastName(''); 
        setGuestClubName('');
      } catch (err) { 
        return toast.error(err.response?.data?.message || 'Fehler beim Anlegen des Gastspielers.'); 
      }
    }
    
    if (!playerIdToEnter || !selectedClassification || !selectedGameMode) { 
      return toast.error("Bitte Spieler, Wertung und Spielmodus auswählen."); 
    }
    
    if (PAIR_GAME_MODES.includes(selectedGameMode) && !player2IdToEnter) { 
      return toast.error("Bitte einen zweiten Spieler für den Paarbewerb auswählen."); 
    }
    
    if (playerIdToEnter === player2IdToEnter) { 
      return toast.error("Spieler 1 und Spieler 2 dürfen nicht identisch sein."); 
    }
    
    try {
      const payload = { player: playerIdToEnter, player2: player2IdToEnter, classification: selectedClassification, team: selectedTeam !== '' ? selectedTeam : null, gameMode: selectedGameMode };
      await apiClient.post(`/api/tournaments/${tournamentId}/entries`, payload);
      // Selektierungen nach Erfolg zurücksetzen
      setSelectedPlayer(''); 
      setSelectedPlayer2('');
      fetchData(); 
      toast.success("Nennung erfolgreich erstellt.");
    } catch (err) { 
      toast.error(err.response?.data?.message || 'Fehler beim Nennen.'); 
    }
  };

  const handleUpdateEntry = async (entryId, updatedData) => {
    try { 
      await apiClient.put(`/api/entries/${entryId}`, updatedData); 
      fetchData(); 
      setEditingEntry(null); 
      toast.success("Nennung erfolgreich aktualisiert."); 
    } catch (err) { 
      toast.error(err.response?.data?.message || "Fehler beim Aktualisieren."); 
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (window.confirm("Diese Nennung wirklich löschen?")) {
      try { 
        await apiClient.delete(`/api/entries/${entryId}`); 
        fetchData(); 
        toast.success("Nennung erfolgreich gelöscht."); 
      } catch (err) { 
        toast.error(err.response?.data?.message || "Fehler beim Löschen."); 
      }
    }
  };

  if (loading) return <p>Lade Turnierdetails...</p>;
  if (!tournament) return <p className="error-message">Turnier nicht gefunden.</p>;

  const EntryItem = ({ entry }) => {
    if (!entry.player) return <li key={entry._id} style={{ color: 'red' }}>Ungültige Nennung</li>;
    const player2Name = entry.player2 ? ` / ${entry.player2.lastName}, ${entry.player2.firstName}` : '';
    return (
      <li key={entry._id}>
        <span>{entry.player.lastName}, {entry.player.firstName}{player2Name} <em>({getLabel(entry.gameMode, GAME_MODES)})</em></span>
        <span className="entry-actions">
          <button className="btn-edit-small" onClick={() => setEditingEntry(entry)}>✎</button>
          <button className="btn-delete-small" onClick={() => handleDeleteEntry(entry._id)}>X</button>
        </span>
      </li>
    );
  };

  return (
    <div className="screen-container">
      {editingEntry && ( <EditEntryModal entry={editingEntry} tournament={tournament} availableClassifications={singleClassifications} teams={teams} players={players} onClose={() => setEditingEntry(null)} onSave={handleUpdateEntry} /> )}
      {editingTeam && ( <EditTeamModal team={editingTeam} tournament={tournament} onClose={() => setEditingTeam(null)} onSave={handleUpdateTeam} /> )}
      <h2>{tournament.name} - Nennungen</h2>
      <div className="summary-box">
        <div className="summary-item"><span className="summary-value">{teamCount}</span><span className="summary-label">Mannschaften</span></div>
        <div className="summary-item"><span className="summary-value">{singleCount}</span><span className="summary-label">Einzelstarter</span></div>
        <div className="summary-item"><span className="summary-value">{entries.length}</span><span className="summary-label">Nennungen Gesamt</span></div>
      </div>
      <div className="layout-grid">
        <div className="grid-column">
          <h3>Mannschaften verwalten</h3>
          <div className="form-container-box">
            <h4>Neue Mannschaft mit Spielern anlegen</h4>
            <form onSubmit={handleCreateTeamWithPlayers}>
              <div className="form-group"><label>Mannschaftsname</label><input type="text" value={newTeamName} onChange={e => setNewTeamName(e.target.value)} /></div>
              <div className="form-group"><label>Mannschafts-Wertung</label><select value={newTeamClassification} onChange={e => setNewTeamClassification(e.target.value)}>{teamClassifications.map(cKey => <option key={cKey} value={cKey}>{getLabel(cKey, CLASSIFICATIONS)}</option>)}</select></div>
              <div className="form-group"><label>Einzel-Wertung für alle Spieler dieser Mannschaft</label><select value={newTeamSingleClassification} onChange={e => setNewTeamSingleClassification(e.target.value)}>{singleClassifications.map(cKey => <option key={cKey} value={cKey}>{getLabel(cKey, CLASSIFICATIONS)}</option>)}</select></div>
              <div className="form-group"><label>Spieler (Vor- und Nachname)</label>{newTeamPlayers.map((player, index) => (<input key={index} type="text" placeholder={`Spieler ${index + 1}`} value={player} onChange={(e) => handleTeamPlayerNameChange(index, e.target.value)} style={{ marginBottom: '0.5rem' }}/>))}</div>
              <button type="submit">Mannschaft anlegen</button>
            </form>
          </div>
          {teams.map(team => {
            const isExpanded = !!expandedTeams[team._id];
            return (
              <div key={team._id} className="team-box">
                <div className="team-header clickable" onClick={() => toggleTeamExpansion(team._id)}>
                  <span><span className={`arrow ${isExpanded ? 'down' : 'right'}`}></span><strong>{team.name}</strong> ({getLabel(team.classification, CLASSIFICATIONS)})</span>
                  <span className="entry-actions">
                    <button className="btn-edit-small" onClick={(e) => { e.stopPropagation(); setEditingTeam(team); }}>✎</button>
                    <button className="btn-delete-small" onClick={(e) => { e.stopPropagation(); handleDeleteTeam(team._id); }}>X</button>
                  </span>
                </div>
                {isExpanded && (<ul>{entries.filter(e => e.team?._id === team._id).map(entry => <EntryItem key={entry._id} entry={entry} />)}</ul>)}
              </div>
            )
          })}
        </div>
        <div className="grid-column">
          <h3>Einzel- & Paarnennungen verwalten</h3>
          <div className="form-container-box">
             <div className="form-toggle-buttons">
                <button className={!isCreatingGuest ? 'active' : ''} onClick={() => setIsCreatingGuest(false)}>Stammspieler nennen</button>
                <button className={isCreatingGuest ? 'active' : ''} onClick={() => setIsCreatingGuest(true)}>Neuen Gastspieler anlegen</button>
             </div>
             <form onSubmit={handleCreateEntry}>
              {isCreatingGuest ? (
                <>
                  <div className="form-group"><label>Vorname*</label><input type="text" value={guestFirstName} onChange={e => setGuestFirstName(e.target.value)} /></div>
                  <div className="form-group"><label>Nachname*</label><input type="text" value={guestLastName} onChange={e => setGuestLastName(e.target.value)} /></div>
                  <div className="form-group"><label>Verein (optional)</label><input type="text" value={guestClubName} onChange={e => setGuestClubName(e.target.value)} placeholder="Name des Gastvereins" /></div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Spieler 1</label>
                    <select value={selectedPlayer} onChange={e => setSelectedPlayer(e.target.value)}>
                      <option value="">-- Ersten Spieler wählen --</option>
                      {unassignedStammPlayers.map(p => <option key={p._id} value={p._id}>{p.lastName}, {p.firstName}</option>)}
                    </select>
                  </div>
                  {PAIR_GAME_MODES.includes(selectedGameMode) && (
                    <div className="form-group">
                      <label>Spieler 2</label>
                      <select value={selectedPlayer2} onChange={e => setSelectedPlayer2(e.target.value)}>
                        <option value="">-- Zweiten Spieler wählen --</option>
                        {unassignedStammPlayersForPair.map(p => <option key={p._id} value={p._id}>{p.lastName}, {p.firstName}</option>)}
                      </select>
                    </div>
                  )}
                </>
              )}
              <div className="form-group"><label>Spielmodus</label><select value={selectedGameMode} onChange={(e) => { setSelectedGameMode(e.target.value); setSelectedPlayer(''); setSelectedPlayer2(''); }}>{availableGameModes.map(gmKey => <option key={gmKey} value={gmKey}>{getLabel(gmKey, GAME_MODES)}</option>)}</select></div>
              <div className="form-group"><label>Wertung</label><select value={selectedClassification} onChange={e => setSelectedClassification(e.target.value)}>{singleClassifications.map(cKey => <option key={cKey} value={cKey}>{getLabel(cKey, CLASSIFICATIONS)}</option>)}</select></div>
              <div className="form-group"><label>Zu Mannschaft hinzufügen (optional)</label><select value={selectedTeam} onChange={e => setSelectedTeam(e.target.value)}><option value="">-- Einzelstarter --</option>{teams.map(team => (entries.filter(e => e.team?._id === team._id).length < 4 &&<option key={team._id} value={team._id}>{team.name}</option>))}</select></div>
              <button type="submit">Nennung erstellen</button>
            </form>
          </div>
          <h3>Genannte Starter (Einzel & Paar)</h3>
           <ul>{entries.filter(e => !e.team).map(entry => <EntryItem key={entry._id} entry={entry} />)}</ul>
        </div>
      </div>
    </div>
  );
};

export default TournamentDetailScreen;